"use client";

import React, { useEffect, useRef, useState } from "react";
import { Phone, CalendarCheck, Banknote, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Call for Quote",
  description: "Dial 778-385-3007 for a free, instant cash offer based on your vehicle's details.",
  icon: Phone
}, {
  title: "Schedule Pickup",
  description: "We arrange a free, same-day towing service anywhere in our BC service areas.",
  icon: CalendarCheck
}, {
  title: "Get Paid",
  description: "Receive your cash on the spot as soon as our driver arrives to remove your junk car.",
  icon: Banknote
}];
export default function ThreeSimpleStepsToCash() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const start = windowHeight * 0.8;
      const end = windowHeight * 0.2;
      const distance = rect.height + start - end;
      const scrolled = start - rect.top;
      let p = scrolled / distance * 100;
      p = Math.max(0, Math.min(100, p));
      setProgress(p);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="3-simple-steps-to-cash" ref={sectionRef} className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden z-0">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none -z-20" />

      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6 uppercase font-serif">
            3 Simple Steps to <span className="text-primary">Cash</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Our process is designed for maximum velocity. Turn your unwanted vehicle into cash today with zero hassle and free towing included.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute left-[2.35rem] top-4 bottom-4 w-0.5 border-l-2 border-dashed border-muted-foreground/30 md:hidden -z-10" />
          {}
          <div className="absolute left-[2.35rem] top-4 w-0.5 bg-primary shadow-[0_0_10px_var(--color-primary)] transition-all duration-150 ease-out md:hidden -z-10" style={{
          height: `${progress}%`
        }} />

          {}
          <div className="hidden md:block absolute top-12 left-12 right-12 h-0.5 border-t-2 border-dashed border-muted-foreground/30 -z-10" />
          {}
          <div className="hidden md:block absolute top-12 left-12 h-0.5 bg-primary shadow-[0_0_10px_var(--color-primary)] transition-all duration-150 ease-out -z-10" style={{
          width: `${progress}%`
        }} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {steps.map((step, i) => <StepItem key={i} step={step} index={i} />)}
          </div>
        </div>

        <div className="mt-20 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button size="lg" className="w-full sm:w-auto text-lg h-14 px-8 font-bold uppercase tracking-wide shadow-[0_0_20px_rgba(132,255,0,0.2)] hover:shadow-[0_0_30px_rgba(132,255,0,0.4)] transition-shadow" asChild>
            <Link href="tel:778-385-3007">
              <Phone className="mr-2 size-5" />
              Call 778-385-3007 Now
            </Link>
          </Button>
          <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg h-14 px-8 font-bold uppercase tracking-wide border-primary/50 hover:bg-primary/10" asChild>
            <Link href="/ContactPage">
              Get Online Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}
function StepItem({
  step,
  index
}: {
  step: any;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={`relative flex flex-row md:flex-col items-start md:items-center gap-6 md:gap-8 transition-all duration-700 delay-[${index * 200}ms] transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute -top-6 left-16 md:-top-12 md:left-1/2 md:-translate-x-1/2 text-7xl md:text-9xl font-serif font-black text-transparent [-webkit-text-stroke:2px_var(--color-muted-foreground)] opacity-10 select-none pointer-events-none z-[-1]">
        0{index + 1}
      </div>

      {}
      <div className="relative shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-card border-2 border-primary/50 flex items-center justify-center text-primary shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-sm group hover:border-primary hover:shadow-[0_0_25px_rgba(132,255,0,0.3)] transition-all duration-300">
        <div className="absolute inset-0 bg-primary/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
        <step.icon className="w-8 h-8 md:w-10 md:h-10 relative z-10" />
        
        {}
        <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shadow-lg">
          {index + 1}
        </div>
      </div>

      {}
      <div className="flex-1 md:text-center pt-2 md:pt-0">
        <h3 className="text-xl md:text-2xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors">
          {step.title}
        </h3>
        <p className="text-muted-foreground leading-relaxed">
          {step.description}
        </p>
      </div>
    </div>;
}